import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Check, AlertCircle, Loader2, ArrowRight, X, Home } from "lucide-react";
import {
  POLICY_REVIEW_CONSENT_TEXT,
  POLICY_REVIEW_MARKETING_TEXT,
  ALLOWED_ACCEPT,
  validatePolicyForm,
  validatePolicyFile,
  submitPolicyReview,
  formatBytes,
} from "@/lib/policyReview";

const labelCls = "block text-[0.7rem] font-semibold tracking-[0.15em] uppercase text-bronze";
const optCls = "text-slate/60 font-normal normal-case tracking-normal";
const inputCls =
  "mt-2 w-full bg-transparent border-b border-taupe/50 py-2.5 text-[0.95rem] text-legacy placeholder:text-slate/50 focus:outline-none focus:border-gold transition-colors";

export const PolicyReviewForm = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [insuranceCompany, setInsuranceCompany] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  // Stable per-form-instance key so double-clicks/retries never duplicate.
  const idempotencyKey = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `pr-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const trimmed = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.trim(),
    consent,
    file,
  };
  const validationErrors = useMemo(
    () => validatePolicyForm(trimmed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstName, lastName, email, consent, file]
  );
  const isValid = Object.keys(validationErrors).length === 0;

  const showErrors = () => setErrors(validationErrors);

  const onFileChange = (selected) => {
    if (!selected) {
      setFile(null);
      return;
    }
    const fileError = validatePolicyFile(selected);
    if (fileError) {
      setErrors((prev) => ({ ...prev, file: fileError }));
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.file;
      return next;
    });
    setFile(selected);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors(validationErrors);
    if (!isValid) return;
    setStatus("submitting");
    const data = await submitPolicyReview(
      {
        firstName: trimmed.firstName,
        lastName: trimmed.lastName,
        email: trimmed.email,
        phone: phone.trim(),
        insuranceCompany: insuranceCompany.trim(),
        message: message.trim(),
        consent,
        marketingOptIn,
        company,
        idempotencyKey: idempotencyKey.current,
      },
      file
    );
    setResult(data);
    if (data.status === "success") {
      setStatus("success");
    } else {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="policy-review-success"
        className="rounded-card bg-[#EFE8D9] p-8 md:p-10 text-center"
      >
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold text-legacy">
          <Check size={26} />
        </span>
        <h3 className="mt-6 font-serif text-2xl md:text-3xl text-legacy leading-tight">
          Your policy review request has been received.
        </h3>
        <p className="mt-4 text-slate text-base leading-relaxed">
          We’ll review your information and follow up within two business days.
        </p>
        <Link to="/" data-testid="policy-review-return-home" className="btn-base btn-primary mt-8 text-[0.8rem] tracking-[0.14em] uppercase">
          <Home size={16} />
          Return Home
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="policy-review-form"
      className="rounded-card bg-[#EFE8D9] p-7 md:p-9"
    >
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="pr-first" className={labelCls}>First Name</label>
          <input
            id="pr-first" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            onBlur={showErrors} placeholder="Your first name" autoComplete="given-name" required
            aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? "pr-first-err" : undefined}
            data-testid="pr-first" className={inputCls}
          />
          {errors.firstName && <p id="pr-first-err" role="alert" className="mt-1.5 text-[0.75rem] text-bronze">{errors.firstName}</p>}
        </div>
        <div>
          <label htmlFor="pr-last" className={labelCls}>Last Name</label>
          <input
            id="pr-last" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
            onBlur={showErrors} placeholder="Your last name" autoComplete="family-name" required
            aria-invalid={Boolean(errors.lastName)} aria-describedby={errors.lastName ? "pr-last-err" : undefined}
            data-testid="pr-last" className={inputCls}
          />
          {errors.lastName && <p id="pr-last-err" role="alert" className="mt-1.5 text-[0.75rem] text-bronze">{errors.lastName}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-6">
        <div>
          <label htmlFor="pr-email" className={labelCls}>Email</label>
          <input
            id="pr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onBlur={showErrors} placeholder="you@email.com" autoComplete="email" required
            aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "pr-email-err" : undefined}
            data-testid="pr-email" className={inputCls}
          />
          {errors.email && <p id="pr-email-err" role="alert" className="mt-1.5 text-[0.75rem] text-bronze">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="pr-phone" className={labelCls}>Phone <span className={optCls}>(optional)</span></label>
          <input
            id="pr-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 555-5555" autoComplete="tel" data-testid="pr-phone" className={inputCls}
          />
        </div>
      </div>

      <div className="mt-6">
        <label htmlFor="pr-company" className={labelCls}>Current Insurance Company <span className={optCls}>(optional)</span></label>
        <input
          id="pr-company" type="text" value={insuranceCompany} onChange={(e) => setInsuranceCompany(e.target.value)}
          placeholder="e.g. Northwestern Mutual, MassMutual, NYL…" data-testid="pr-insurance" className={inputCls}
        />
      </div>

      <div className="mt-6">
        <label htmlFor="pr-message" className={labelCls}>A Few Words About What You’d Like Reviewed <span className={optCls}>(optional)</span></label>
        <textarea
          id="pr-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
          placeholder="Anything you’d like me to know about this policy or your situation."
          data-testid="pr-message" className={`${inputCls} resize-none`}
        />
      </div>

      {/* Honeypot — visually hidden, must remain empty */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="pr-hp">Company</label>
        <input id="pr-hp" type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
      </div>

      {/* Upload */}
      <div className="mt-8">
        <label htmlFor="pr-file" className={labelCls}>Upload Your Policy · PDF, DOC, or Image, up to 10MB <span className={optCls}>(optional)</span></label>
        <input
          ref={fileInputRef} id="pr-file" type="file" accept={ALLOWED_ACCEPT} className="sr-only"
          data-testid="pr-file" onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        />
        {file ? (
          <div data-testid="pr-file-selected" className="mt-3 flex items-center justify-between gap-3 rounded-card border border-taupe/60 bg-ivory/60 px-4 py-3">
            <span className="truncate text-[0.85rem] text-legacy">{file.name} <span className="text-slate/60">({formatBytes(file.size)})</span></span>
            <button type="button" onClick={() => onFileChange(null)} aria-label="Remove file" data-testid="pr-file-remove" className="shrink-0 text-slate hover:text-bronze">
              <X size={18} />
            </button>
          </div>
        ) : (
          <button
            type="button" onClick={() => fileInputRef.current?.click()} data-testid="pr-file-trigger"
            className="mt-3 w-full rounded-card border border-dashed border-taupe/70 bg-ivory/40 px-6 py-10 flex flex-col items-center justify-center gap-3 text-center hover:border-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold transition-colors"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-taupe/60 text-bronze"><Upload size={18} /></span>
            <span className="text-[0.9rem] text-slate">Click to upload your policy document</span>
          </button>
        )}
        {errors.file && <p role="alert" data-testid="pr-file-err" className="mt-1.5 text-[0.75rem] text-bronze">{errors.file}</p>}
        <p className="mt-3 text-[0.72rem] leading-relaxed text-slate/80">
          Your document is submitted privately. Before uploading, consider redacting Social Security
          numbers, bank account numbers, medical records, and other information that is not necessary
          for the review.
        </p>
      </div>

      {/* Consent */}
      <label className="flex items-start gap-2.5 mt-7 cursor-pointer">
        <input
          type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); showErrors(); }}
          required aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? "pr-consent-err" : undefined}
          data-testid="pr-consent" className="mt-1 h-4 w-4 shrink-0 rounded border-taupe/70 accent-[#8B6241]"
        />
        <span className="text-[0.78rem] leading-relaxed text-slate/90">
          {POLICY_REVIEW_CONSENT_TEXT}{" "}
          See our <Link to="/privacy" className="text-bronze underline hover:text-gold">Privacy Policy</Link>.
        </span>
      </label>
      {errors.consent && <p id="pr-consent-err" role="alert" data-testid="pr-consent-err" className="mt-1.5 text-[0.75rem] text-bronze">{errors.consent}</p>}

      {/* Optional, separate marketing opt-in — NOT the required authorization consent */}
      <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
        <input
          type="checkbox" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)}
          data-testid="pr-marketing" className="mt-1 h-4 w-4 shrink-0 rounded border-taupe/70 accent-[#8B6241]"
        />
        <span className="text-[0.78rem] leading-relaxed text-slate/90">{POLICY_REVIEW_MARKETING_TEXT}</span>
      </label>

      {status === "error" && (
        <p role="alert" data-testid="pr-error" className="mt-5 flex items-start gap-2 text-[0.8rem] text-bronze">
          <AlertCircle size={15} className="shrink-0 mt-0.5" />{result?.message || "Something went wrong. Please try again."}
        </p>
      )}

      <button
        type="submit" disabled={status === "submitting" || !isValid} data-testid="pr-submit"
        className="btn-base btn-outline-dark w-full mt-8 text-[0.8rem] tracking-[0.14em] uppercase disabled:opacity-60"
      >
        {status === "submitting" ? <><Loader2 size={16} className="animate-spin" />Submitting…</> : <>Request Policy Review<ArrowRight size={16} /></>}
      </button>

      <p className="mt-4 text-[0.72rem] text-slate/70">Your document is sent privately. No third parties.</p>
    </form>
  );
};
